<template>
  <div>
    <!-- SEARCH INPUT -->
    <div class="max-w-144 flex-1">
      <label class="mb-1.5 block text-grey-300 typo-subtitle" for="product-search">{{ $t('search_products') }}</label>
      <SearchInput
        id="product-search"
        :value="searchQuery"
        class="mb-6"
        @input="updateSearchQuery"
      />
    </div>

    <!-- FILTER BUTTON MOBILE -->
    <AppButton
      class="mb-6 w-full sm:hidden"
      :aria-label="$t('aria_labels.show_filter')"
      :aria-expanded="showMobileFilter"
      aria-controls="mobile-filter-modal"
      @click="showHideMobileFilter(!showMobileFilter)"
    >
      {{ $t('filter') }}
    </AppButton>

    <!-- FILTERS DESKTOP -->
    <div ref="filterListElement" class="transition-[height] duration-300 ease-in-out">
      <template v-if="categories.length">
        <label class="mb-1.5 block text-grey-300 typo-subtitle max-sm:hidden" for="product-search">{{ $t('categories') }}</label>
        <TransitionGroup
          tag="ul"
          name="list"
          class="mb-6 grid grid-cols-[repeat(auto-fill,_minmax(32rem,36rem))] gap-x-7 gap-y-5 max-sm:hidden"
        >
          <li key="categories">
            <FilterProducts
              v-model="categoriesModel"
              :is-single-select="catIsSingleSelect"
              is-category-filter
              :category-options="categories"
              :label="$t('category')"
            />
          </li>

          <li v-if="subCategories.length" key="subCategories">
            <FilterProducts
              v-model="subCategoriesModel"
              is-category-filter
              :category-options="subCategories"
              :label="$t('sub_category')"
            />
          </li>
        </TransitionGroup>
      </template>
      <template v-if="propertyFilters.length">
        <label class="mb-1.5 block text-grey-300 typo-subtitle max-sm:hidden" for="product-search">{{ $t('properties') }}</label>
        <TransitionGroup
          tag="ul"
          name="list"
          class="grid grid-cols-[repeat(auto-fill,_minmax(32rem,36rem))] gap-x-7 gap-y-5 max-sm:hidden"
        >
          <li
            v-for="filter in propertyFilters.slice(0, currentAmountFilters)"
            :key="filter.id"
          >
            <FilterProducts
              v-model="properties[filter.id]"
              :property-options="filter"
            />
          </li>

          <li v-if="propertyFilters.length > initAmountFilters">
            <AppButton
              key="more-filters"
              variant="secondary"
              class="h-[3.75rem] w-full"
              @click="setAmountFilters()"
            >
              {{ currentAmountFilters === initAmountFilters ? $t('more_filters') : $t('less_filters') }}
            </AppButton>
          </li>
        </TransitionGroup>
      </template>
    </div>

    <!-- FILTERS MOBILE -->
    <Transition
      enter-active-class="transition duration-100 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition delay-500 duration-100 ease-out"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      @after-enter="mobileFilterVisible = true"
      @before-leave="mobileFilterVisible = false"
    >
      <div
        v-show="showMobileFilter"
        id="mobile-filter-modal"
        :aria-labelledby="$t('filter')"
        aria-modal="true"
        class="fixed inset-0 z-30 flex overflow-auto pt-[calc(var(--navbar-height)+3rem)]"
      >
        <div
          class="mt-auto bg-white px-container py-12 shadow-100 transition-transform duration-500 ease-in-out"
          :class="mobileFilterVisible ? 'translate-y-0' : 'translate-y-full'"
        >
          <!-- HEADER MOBILE -->
          <div class="mb-7 flex items-center justify-between gap-10">
            <!-- Title -->
            <h3 class="font-semibold typo-lead">
              {{ $t('filter') }}
            </h3>

            <!-- Close Button -->
            <button type="button" :aria-label="$t('aria_labels.close_filter')" @click="showHideMobileFilter(false)">
              <IconClose class="size-7" />
            </button>
          </div>

          <!-- Filters Mobile -->
          <template v-if="categories.length">
            <label class="mb-1.5 block text-grey-300 typo-subtitle" for="product-search">{{ $t('categories') }}</label>
            <ul class="mb-6 grid grid-cols-1 gap-x-7 gap-y-5 3xl:grid-cols-3 sm:grid-cols-2">
              <li>
                <FilterProducts
                  v-model="categoriesModel"
                  :is-single-select="catIsSingleSelect"
                  is-category-filter
                  :category-options="categories"
                  :label="$t('category')"
                />
              </li>

              <li v-if="subCategories.length">
                <FilterProducts
                  v-model="subCategoriesModel"
                  is-category-filter
                  :category-options="subCategories"
                  :label="$t('sub_category')"
                />
              </li>
            </ul>
          </template>
          <template v-if="propertyFilters.length">
            <label class="mb-1.5 block text-grey-300 typo-subtitle" for="product-search">{{ $t('properties') }}</label>
            <ul class="mb-6 grid grid-cols-1 gap-x-7 gap-y-5 3xl:grid-cols-3 sm:grid-cols-2">
              <li
                v-for="(filter, index) in propertyFilters.slice(0, currentAmountFilters)"
                :key="index"
              >
                <FilterProducts
                  v-model="properties[filter.name]"
                  :property-options="filter"
                />
              </li>
            </ul>
          </template>
          <AppButton
            variant="secondary"
            class="mb-5 mt-8 w-full"
            @click="resetFilters()"
          >
            {{ $t('filter_revert') }}
          </AppButton>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { useScrollLock } from '@vueuse/core'
import { getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'

type ListingFilter = {
  label: string
  code: string
  id: string
  name: string
}

export type Filter = {
  id: string
  label: string
  code: string
  name: string
  apiAlias?: string
  options?: {
    id?: string
    name?: string
  }[]
}

const props = defineProps<{
  availableFilters: ListingFilter[]
  categories: Schemas['Category'][]
  subCategories: Schemas['Category'][]
}>()

const initAmountFilters = 3
const currentAmountFilters = ref(initAmountFilters)
const preselectedFilterId = '019716df767b7394a19989148879565b' // ID of "Verkaufseinheit" property-filter
const preselectedOptionId = '019716df783971f082915ba14f8927b6' // ID of "1 Stk. = 10 l" filter-option

const showMobileFilter = ref(false)
const mobileFilterVisible = ref(false)

const criteria = defineModel<Schemas['ProductListingCriteria']>()

const propertyFilters = computed<Filter[]>(() => {
  const propertyFiltersUnsorted = props.availableFilters.filter(
    filter => filter.code === 'properties',
  ).map(filter => filter as Filter)

  // sort filters to show preselected filter first
  const sortedPropertyFilters = [...propertyFiltersUnsorted].sort((a, b) => {
    if (a.id === preselectedFilterId) return -1
    if (b.id === preselectedFilterId) return 1
    return 0
  })

  // sort options to show preselected option first
  const sortedOptions = sortedPropertyFilters.find(f => f.id === preselectedFilterId)
  if (sortedOptions?.options) {
    sortedOptions.options = [...sortedOptions.options].sort((a, b) => {
      if (a.id === preselectedOptionId) return -1
      if (b.id === preselectedOptionId) return 1
      return 0
    })
  }

  return sortedPropertyFilters
})

const catIsSingleSelect = computed<boolean>(() => {
  return props.categories.some(cat => cat.childCount > 0)
})

const properties = ref(reduceProperties())

const categoriesModel = ref([] as string[])
const subCategoriesModel = ref([] as string[])

const searchQuery = ref('')

function reduceProperties() {
  return propertyFilters.value.reduce(
    (acc, filter) => {
      acc[filter.name] = []
      return acc
    },
    {} as Record<string, string[]>,
  )
}

function showHideMobileFilter(value: boolean) {
  showMobileFilter.value = value

  if (value) {
    isLocked.value = true
  } else {
    isLocked.value = false
  }
}

function resetFilters() {
  properties.value = reduceProperties()
  categoriesModel.value = []
  searchQuery.value = ''
  maybeApplyPreselectedOption()
}

function updateSearchQuery(value: string) {
  searchQuery.value = value
  updateCriteria()
}

function updateCriteria() {
  const filters = [...criteria.value?.filter?.filter(f => f.type !== 'multi') ?? []]

  if (searchQuery.value) {
    filters.push({
      type: 'multi',
      queries: [
        {
          type: 'contains',
          field: 'productNumber',
          value: searchQuery.value,
        },
        {
          type: 'contains',
          field: 'name',
          value: searchQuery.value,
        },
      ],
      operator: 'OR',
    })
  }

  criteria.value = {
    ...criteria.value,
    filter: filters,
  }
}

const filterListElement = ref<HTMLElement>()
const initialHeight = ref(0)
async function setAmountFilters() {
  // Define the amount of filters to show
  const isShowingAllFilters = currentAmountFilters.value === initAmountFilters
  currentAmountFilters.value = isShowingAllFilters ? propertyFilters.value.length : initAmountFilters

  // The next part is height animation
  if (!filterListElement.value) {
    return
  }

  if (isShowingAllFilters) {
    initialHeight.value = filterListElement.value.scrollHeight
  }

  filterListElement.value.style.height = `${filterListElement.value.scrollHeight}px`

  await nextTick()

  if (isShowingAllFilters) {
    // Expand
    filterListElement.value.style.height = `${filterListElement.value.scrollHeight}px`
  } else {
    // Collapse
    filterListElement.value.style.height = `${initialHeight.value}px`
  }

  // Wait for the animation to finish
  await new Promise(resolve => setTimeout(resolve, 300))

  // Reset the height to auto
  filterListElement.value.style.height = 'auto'
}

function maybeApplyPreselectedOption() {
  const filter = propertyFilters.value.find(f => f.id === preselectedFilterId)
  const option = filter?.options?.find(opt => opt.id === preselectedOptionId)

  if (option && !properties.value[preselectedFilterId]?.length) {
    properties.value[preselectedFilterId] = [
      {
        value: option.id,
        label: getTranslatedProperty(option, 'name'),
        optionsLabel: getTranslatedProperty(option, 'name'),
      },
    ]
  }
}

watchDeep(properties, (value) => {
  criteria.value = {
    ...criteria.value,
    properties: Object.values(value)
      // Filter out empty categories
      .filter(property => property.length)
      // map the value of each property to a string and join them
      .map(property => property.map(item => item.value).join('|'))
      // join the categories
      .join('|'),
  }
})

const isResetingSubCategories = ref(false)

watch(categoriesModel, (value) => {
  isResetingSubCategories.value = true
  subCategoriesModel.value = []

  const ids = Object.values(value)
    .map(category => category.value)
    .filter(Boolean) // Remove any falsy values
    .join('|')

  const filter: any[] = []

  if (ids) {
    filter.push({
      type: 'equalsAny',
      field: 'categoryTree',
      value: ids,
    })
  }

  criteria.value = {
    ...criteria.value,
    filter,
  }
})

watch(subCategoriesModel, (value) => {
  if (isResetingSubCategories.value) {
    isResetingSubCategories.value = false
    return
  }

  const ids = Object.values(value)
    .map(category => category.value)
    .filter(Boolean) // Remove any falsy values
    .join('|')

  const filter = [...criteria.value?.filter || []]
  if (ids) {
    filter[1] = {
      type: 'equalsAny',
      field: 'categoryTree',
      value: ids,
    }
  } else if (filter.length === 2) {
    filter.pop()
  }

  criteria.value = {
    ...criteria.value,
    filter,
  }
})

const body = ref<HTMLElement>()
let isLocked: any

onMounted(() => {
  // Since document.body is available only client-side, assign it here
  body.value = document.body
  isLocked = useScrollLock(body)
  maybeApplyPreselectedOption()
})

onUnmounted(() => {
  // Ensure the body is unlocked when the component is unmounted
  if (isLocked) {
    isLocked.value = false
  }
})
</script>
